
import { useRef, useCallback, useEffect } from 'react';
import { useAnalytics } from './useAnalytics';

interface VideoAnalyticsData {
  postId: string;
  videoUrl?: string;
  cropType?: string;
  userName?: string;
}

export const useVideoAnalytics = (videoData: VideoAnalyticsData) => {
  const { 
    logVideoPlay, 
    logVideoPause, 
    logVideoMuteToggle, 
    logVideoCompletion, 
    logVideoSkip,
    logVideoEngagement 
  } = useAnalytics();
  
  const startTimeRef = useRef<number>(0);
  const lastProgressRef = useRef<number>(0);
  const hasLoggedPlayRef = useRef<boolean>(false);
  const watchDurationRef = useRef<number>(0);

  const trackVideoPlay = useCallback(async (autoplay: boolean = false) => {
    if (!hasLoggedPlayRef.current) {
      startTimeRef.current = Date.now();
      hasLoggedPlayRef.current = true;
      
      await logVideoPlay({
        post_id: videoData.postId,
        video_url: videoData.videoUrl,
        crop_type: videoData.cropType,
        user_name: videoData.userName,
        autoplay,
        timestamp: 0
      });
    }
  }, [videoData, logVideoPlay]);

  const trackVideoPause = useCallback(async (currentTime: number) => {
    if (hasLoggedPlayRef.current) {
      const sessionDuration = Date.now() - startTimeRef.current;
      watchDurationRef.current += sessionDuration;
      
      await logVideoPause({
        post_id: videoData.postId,
        video_url: videoData.videoUrl,
        watch_duration: watchDurationRef.current,
        timestamp: currentTime
      });
      
      hasLoggedPlayRef.current = false;
    }
  }, [videoData, logVideoPause]);

  const trackMuteToggle = useCallback(async (isMuted: boolean, currentTime: number) => {
    await logVideoMuteToggle({
      post_id: videoData.postId,
      video_url: videoData.videoUrl,
      is_muted: isMuted,
      timestamp: currentTime
    });
  }, [videoData, logVideoMuteToggle]);

  const trackVideoProgress = useCallback(async (currentTime: number, duration: number) => {
    const progress = (currentTime / duration) * 100;
    
    // Track completion at 90% to account for video ending variations
    if (progress >= 90 && lastProgressRef.current < 90) {
      const totalWatchTime = watchDurationRef.current + (Date.now() - startTimeRef.current);
      
      await logVideoCompletion({
        post_id: videoData.postId,
        video_url: videoData.videoUrl,
        total_duration: duration * 1000, // Convert to milliseconds
        watch_duration: totalWatchTime,
        completion_percentage: progress,
        crop_type: videoData.cropType
      });
    }
    
    lastProgressRef.current = progress;
  }, [videoData, logVideoCompletion]);

  const trackVideoSkip = useCallback(async (currentTime: number, totalDuration?: number) => {
    const sessionDuration = Date.now() - startTimeRef.current;
    const totalWatchTime = watchDurationRef.current + sessionDuration;
    
    await logVideoSkip({
      post_id: videoData.postId,
      video_url: videoData.videoUrl,
      watch_duration: totalWatchTime,
      skip_timestamp: currentTime,
      total_duration: totalDuration ? totalDuration * 1000 : undefined
    });
  }, [videoData, logVideoSkip]);

  const trackEngagement = useCallback(async (
    engagementType: 'scroll_to_view' | 'intersect_start' | 'intersect_end' | 'full_watch' | 'replay',
    additionalData?: { intersectionRatio?: number; viewportTime?: number }
  ) => {
    const watchDuration = hasLoggedPlayRef.current 
      ? watchDurationRef.current + (Date.now() - startTimeRef.current)
      : watchDurationRef.current;

    await logVideoEngagement({
      post_id: videoData.postId,
      video_url: videoData.videoUrl,
      engagement_type: engagementType,
      watch_duration: watchDuration,
      intersection_ratio: additionalData?.intersectionRatio,
      viewport_time: additionalData?.viewportTime
    });
  }, [videoData, logVideoEngagement]);

  // Reset analytics when video data changes
  useEffect(() => {
    startTimeRef.current = 0;
    lastProgressRef.current = 0;
    hasLoggedPlayRef.current = false;
    watchDurationRef.current = 0;
  }, [videoData.postId]);

  return {
    trackVideoPlay,
    trackVideoPause,
    trackMuteToggle,
    trackVideoProgress,
    trackVideoSkip,
    trackEngagement
  };
};
